import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { InputLabel } from "../../../Atoms/InputsCommon/InputLabel";
import { Textarea } from "../../../ui/textarea";

const DEFAULT_ROWS = 4;

export interface RedesignTextAreaProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "value" | "onChange"
  > {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  label?: string;
  annex?: React.ReactNode;
  maxLength?: number;
  /** When length >= this, show warning state (orange). e.g. maxLength - 100 */
  warningThreshold?: number;
  error?: boolean;
  showCharacterCount?: boolean;
  alwaysShowCharacterCount?: boolean;
  resizable?: boolean;
  rows?: number;
  className?: string;
  containerClassName?: string;
  "data-testid"?: string;
}

type VisualState = "default" | "warning" | "error";

function getVisualState(
  value: string,
  maxLength: number | undefined,
  warningThreshold: number | undefined,
  error: boolean
): VisualState {
  const len = value.length;

  if (maxLength !== undefined && len > maxLength) {
    return "error";
  }

  if (error) {
    return "error";
  }

  if (
    maxLength !== undefined &&
    warningThreshold !== undefined &&
    len >= warningThreshold &&
    len <= maxLength
  ) {
    return "warning";
  }

  return "default";
}

export const RedesignTextArea = forwardRef<
  HTMLTextAreaElement,
  RedesignTextAreaProps
>(function RedesignTextArea(
  {
    value = "",
    onValueChange,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    disabled,
    readOnly,
    rows = DEFAULT_ROWS,
    id,
    name,
    maxLength,
    warningThreshold,
    error = false,
    showCharacterCount = false,
    alwaysShowCharacterCount = false,
    resizable = false,
    label,
    annex,
    className,
    containerClassName,
    "data-testid": dataTestId,
    ...rest
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

  const mergedRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null) {
        (ref as { current: HTMLTextAreaElement | null }).current = node;
      }
    },
    [ref]
  );

  const visualState = getVisualState(value, maxLength, warningThreshold, error);

  const showCount =
    showCharacterCount &&
    maxLength !== undefined &&
    (isFocused || alwaysShowCharacterCount);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    },
    [onChange, onValueChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const containerClasses = cx(
    "relative flex flex-col w-full rounded-[10px] overflow-visible",
    "border border-solid",
    "bg-[#fff] border-grey-light-2",
    "dark:bg-white/15 dark:border-white/18",
    "transition-[border-color,box-shadow]",
    isFocused && [
      "outline-none",
      visualState === "default" &&
        "ring-2 ring-blue-focus ring-offset-0 dark:ring-white/30",
      visualState === "warning" &&
        "border-orange-burnt ring-2 ring-orange-burnt ring-offset-0",
      visualState === "error" &&
        "border-red-cardinal ring-2 ring-red-cardinal ring-offset-0",
    ],
    !isFocused && [
      visualState === "warning" && "border-orange-burnt",
      visualState === "error" && "border-red-cardinal",
    ],
    containerClassName
  );

  const textareaClasses = cx(
    "block w-full rounded-[10px] border-0 bg-transparent p-2 text-sm font-normal leading-normal outline-none",
    "text-color-neutral-one placeholder:text-grey-medium-light placeholder:italic dark:text-white/90 dark:placeholder:text-white/60",
    resizable ? "resize-y" : "resize-none",
    showCharacterCount && "min-h-[80px]"
  );

  const characterCountBar =
    showCharacterCount && maxLength !== undefined ? (
      <div className="flex min-h-[22px] flex-shrink-0 items-center justify-end px-2 pb-1 pt-0">
        <span
          className={cx(
            "inline-flex shrink-0 items-center justify-center rounded-[20px] px-[3px] py-0 text-right text-[8px] font-normal leading-normal transition-opacity",
            showCount ? "opacity-75" : "opacity-0",
            visualState === "default" &&
              "text-grey-shark-5 bg-transparent dark:text-white",
            visualState === "warning" && "bg-orange-burnt text-white",
            visualState === "error" && "bg-red-cardinal text-white"
          )}
        >
          {value.length}/{maxLength}
        </span>
      </div>
    ) : null;

  return (
    <div className={cx("flex flex-col gap-1", className)}>
      {label != null && <InputLabel label={label} annex={annex} htmlFor={id} />}
      <div className={cx("relative", containerClasses)}>
        <div className="flex flex-col">
          <Textarea
            ref={mergedRef}
            id={id}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            rows={rows}
            maxLength={maxLength}
            data-testid={dataTestId}
            className={cx(textareaClasses, showCharacterCount && "pb-1")}
            aria-invalid={visualState === "error"}
            {...rest}
          />
          {characterCountBar}
        </div>
      </div>
    </div>
  );
});
